import type { AutomationType } from '@playbook-technologies/common-enums';
import { useMutation } from '@tanstack/react-query';

import type { Async } from '@client/new/helpers/types';

import {
  AutomationStepInsight,
  SendAutomationTestRequest,
  SendAutomationTestResponse,
  sendAutomationTest,
} from '../automations.api';
import { serverErrorMessage } from '../automations.errors';
import {
  AutomationInsightsError,
  useAutomationStatsModel,
} from './AutomationInsights.model';

export type SendTestStep = number | 'all';

export type SendTestInput = {
  step: SendTestStep;
  variant: string;
  recipients: string[];
  userId?: number;
};

export type SendTestModel = {
  steps: AutomationStepInsight[];
  result: Async<SendAutomationTestResponse, string>;
  send: (input: SendTestInput) => Promise<void>;
};

export const toSendTestRequest = (
  input: SendTestInput,
  creatorId: number,
  automationType: AutomationType,
): SendAutomationTestRequest => ({
  automationType,
  step: input.step,
  ...(input.variant ? { variant: input.variant } : {}),
  creatorId,
  to: input.recipients,
  ...(input.userId !== undefined ? { userId: input.userId } : {}),
});

const FALLBACK_MESSAGE = "We couldn't send the test.";

/** creator-api passes messaging's 400/403/404 bodies through verbatim. */
export const sendTestErrorMessage = (error: unknown): string =>
  serverErrorMessage(error) ?? FALLBACK_MESSAGE;

const toResult = (mutation: {
  status: 'idle' | 'loading' | 'error' | 'success';
  data: SendAutomationTestResponse | undefined;
  error: unknown;
}): Async<SendAutomationTestResponse, string> => {
  switch (mutation.status) {
    case 'idle':
      return { type: 'not-fetched' };
    case 'loading':
      return { type: 'fetching' };
    case 'error':
      return {
        type: 'fetch-error',
        value: sendTestErrorMessage(mutation.error),
      };
    case 'success':
      return mutation.data
        ? { type: 'fetched', value: mutation.data }
        : { type: 'not-fetched' };
  }
};

export const useSendTestModel = (
  creatorId: number,
  automationType: AutomationType,
): Async<SendTestModel, AutomationInsightsError> => {
  const stats = useAutomationStatsModel(creatorId);
  const mutation = useMutation<
    SendAutomationTestResponse,
    unknown,
    SendAutomationTestRequest
  >({ mutationFn: sendAutomationTest, retry: false });

  if (stats.type !== 'fetched') return stats;

  const entry = stats.value.stats.find(
    (item) => item.automationType === automationType,
  );

  return {
    type: 'fetched',
    value: {
      steps: entry?.steps ?? [],
      result: toResult(mutation),
      send: async (input) => {
        await mutation
          .mutateAsync(toSendTestRequest(input, creatorId, automationType))
          .catch(() => {
            // Outcome is exposed through `result`; the View renders it inline.
          });
      },
    },
  };
};
